// --- CORRECTED FILE: packages/api-client/src/services/public/publicAgentProfilesService.ts ---

import { getApiClient } from '../../client';
import { handleApiError } from '../../utils/handleApiError';
import type { ServiceResponse, AgentProfile, PaginatedResponse, CalculatorSpec } from '@dopomogai/types';

const PREFIX = '/public/agent-profiles';

interface ListAgentProfilesParams {
  only_defaults?: boolean;
  include_preview?: boolean;
  // --- START OF CHANGE ---
  include_preorder?: boolean; // <-- 1. ADD PARAMETER TO THE INTERFACE
  // --- END OF CHANGE ---
  include_route?: boolean;
  q?: string;
  industry?: string[];
  sort?: 'name' | 'new' | 'popular';
  limit?: number;
  offset?: number;
}

type ListAgentProfilesResponse = PaginatedResponse<AgentProfile>;

export const publicAgentProfilesService = {
  /**
   * Lists public agent profiles with optional filtering and pagination.
   * @param params Optional parameters for filtering and pagination
   * @returns A promise that resolves with the list of agent profiles or an API error.
   */
  listAgentProfiles: (params?: ListAgentProfilesParams): ServiceResponse<ListAgentProfilesResponse> => {
    const qs = new URLSearchParams();
    if (params?.only_defaults) qs.set('only_defaults', 'true');
    if (typeof params?.include_preview === 'boolean') qs.set('include_preview', params.include_preview ? 'true' : 'false');
    // --- START OF CHANGE ---
    if (typeof params?.include_preorder === 'boolean') qs.set('include_preorder', params.include_preorder ? 'true' : 'false'); // <-- 2. ADD PARAMETER TO THE URL BUILDER
    // --- END OF CHANGE ---
    if (typeof params?.include_route === 'boolean') qs.set('include_route', params.include_route ? 'true' : 'false');
    if (params?.q) qs.set('q', params.q);
    if (params?.industry && params.industry.length > 0) params.industry.forEach((i) => qs.append('industry', i));
    if (params?.sort) qs.set('sort', params.sort);
    if (typeof params?.limit === 'number') qs.set('limit', String(params.limit));
    if (typeof params?.offset === 'number') qs.set('offset', String(params.offset));
    const url = qs.toString() ? `${PREFIX}?${qs}` : PREFIX;

    return getApiClient()
      .get<ListAgentProfilesResponse>(url)
      .then(res => {
        const data = res.data as any;
        if (data && Array.isArray(data.items)) {
          data.items = data.items.map((it: any) => {
            const id = it.id ?? it._id;
            const { _id, ...rest } = it;
            return { id, ...rest };
          });
        }
        return data;
      })
      .catch(err => handleApiError(err, 'listing public agent profiles'));
  },

  /**
   * Fetches a specific public agent profile by its ID.
   * @param agentId The ID of the agent profile to retrieve.
   * @returns A promise that resolves with the agent profile data or an API error.
   */
  getAgentProfile: (agentId: string): ServiceResponse<AgentProfile> => {
    return getApiClient()
      .get<AgentProfile>(`${PREFIX}/${encodeURIComponent(agentId)}`)
      .then(res => {
        const raw: any = res.data;
        if (raw && raw._id && !raw.id) {
          const { _id, ...rest } = raw;
          return { id: _id, ...rest } as AgentProfile;
        }
        return raw;
      })
      .catch(err => handleApiError(err, `fetching agent profile '${agentId}'`));
  },

  /**
   * Fetches the calculator specification for a given agent.
   * @param agentId The ID or slug of the agent.
   * @returns A promise that resolves with the calculator spec or an API error.
   */
  getCalculatorSpec: (agentId: string): ServiceResponse<CalculatorSpec> => {
    return getApiClient()
      .get<CalculatorSpec>(`${PREFIX}/${encodeURIComponent(agentId)}/calculator-spec`)
      .then(res => res.data)
      .catch(err => handleApiError(err, `fetching calculator spec for agent ${agentId}`));
  },
};